(function () {
  const page = (window.FoxTheme && window.FoxTheme.page) || {};

  const PredictiveInsights = {
    // Predict stock demand based on viewing patterns
    predictDemand: function (productId) {
      const viewHistory = JSON.parse(localStorage.getItem('ac_viewed_products') || '[]');
      const productViews = viewHistory.filter((p) => p.id === productId);
      const recentViews = productViews.filter(
        (p) => Date.now() - p.timestamp < 24 * 60 * 60 * 1000 // Last 24 hours
      );

      // High demand signal
      if (recentViews.length >= 3) {
        this.signalHighDemand(productId);
      }

      return {
        totalViews: productViews.length,
        recentViews: recentViews.length,
        demandLevel: recentViews.length >= 3 ? 'high' : recentViews.length >= 1 ? 'medium' : 'low',
      };
    },

    // Signal high demand to backend (via custom metafield or webhook)
    signalHighDemand: function (productId) {
      // Track for analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'predictive_demand',
          productId: productId,
          demandLevel: 'high',
          timestamp: Date.now(),
        });
      }
    },

    // Calculate customer lifetime value indicators
    calculateCLVIndicators: function () {
      const viewHistory = JSON.parse(localStorage.getItem('ac_viewed_products') || '[]');
      const cartEvents = JSON.parse(sessionStorage.getItem('ac_cart_events') || '[]');
      const pageSequence = JSON.parse(sessionStorage.getItem('ac_page_sequence') || '[]');

      const indicators = {
        engagement: viewHistory.length,
        intentStrength: cartEvents.length,
        sessionDepth: pageSequence.length,
        categoryDiversity: new Set(viewHistory.map((p) => p.category)).size,
        avgTimePerPage: this.calculateAvgTimePerPage(pageSequence),
      };

      // Score 0-100
      const clvScore = Math.min(
        100,
        indicators.engagement * 5 +
          indicators.intentStrength * 10 +
          indicators.sessionDepth * 3 +
          indicators.categoryDiversity * 8
      );

      return {
        score: clvScore,
        segment: clvScore >= 70 ? 'high_value' : clvScore >= 40 ? 'medium_value' : 'low_value',
        indicators: indicators,
      };
    },

    // Customer segmentation
    segmentCustomer: function () {
      const clv = this.calculateCLVIndicators();
      const intent = sessionStorage.getItem('ac_intent');
      const viewHistory = JSON.parse(localStorage.getItem('ac_viewed_products') || '[]');

      const segment = {
        type: 'browser', // browser, researcher, buyer, loyal
        value: clv.segment,
        intent: intent,
        personalization: {},
      };

      // Determine segment type
      if (clv.score >= 70 && intent === 'checkout_intent') {
        segment.type = 'buyer';
        segment.personalization = {
          showUrgency: true,
          offerDiscount: true,
          prioritizeCheckout: true,
        };
      } else if (viewHistory.length >= 10) {
        segment.type = 'researcher';
        segment.personalization = {
          showComparisons: true,
          detailedInfo: true,
          expertReviews: true,
        };
      } else if (clv.score >= 50) {
        segment.type = 'engaged_browser';
        segment.personalization = {
          recommendSimilar: true,
          showcaseVariety: true,
        };
      }

      // Store segment
      sessionStorage.setItem('customer_segment', JSON.stringify(segment));

      return segment;
    },

    // Price sensitivity detection
    detectPriceSensitivity: function () {
      const viewHistory = JSON.parse(localStorage.getItem('ac_viewed_products') || '[]');

      if (viewHistory.length < 3) return 'unknown';

      // Analyze viewing patterns
      const viewedPriceRanges = viewHistory.map((p) => {
        const price = parseFloat(p.price || 0);
        if (price < 25) return 'budget';
        if (price < 75) return 'mid';
        return 'premium';
      });

      const budgetViews = viewedPriceRanges.filter((r) => r === 'budget').length;
      const totalViews = viewedPriceRanges.length;

      if (budgetViews / totalViews > 0.7) {
        return 'high_sensitivity'; // Show discounts, sales
      } else if (budgetViews / totalViews < 0.3) {
        return 'low_sensitivity'; // Focus on quality, features
      }

      return 'medium_sensitivity';
    },

    // Calculate average time per page
    calculateAvgTimePerPage: function (sequence) {
      if (sequence.length < 2) return 0;

      let totalTime = 0;
      for (let i = 1; i < sequence.length; i++) {
        totalTime += sequence[i].timestamp - sequence[i - 1].timestamp;
      }

      return Math.round(totalTime / (sequence.length - 1) / 1000); // in seconds
    },

    // Predict next best action
    predictNextAction: function () {
      const segment = this.segmentCustomer();
      const priceSensitivity = this.detectPriceSensitivity();
      const intent = sessionStorage.getItem('ac_intent');

      const predictions = {
        recommendedActions: [],
        messaging: {},
        offers: [],
      };

      // Action recommendations based on segment and intent
      if (segment.type === 'buyer' && priceSensitivity === 'high_sensitivity') {
        predictions.recommendedActions.push('show_discount');
        predictions.messaging.primary = 'Limited time: 10% off your order';
        predictions.offers.push({ type: 'percentage', value: 10, code: 'SAVE10' });
      }

      if (segment.type === 'researcher') {
        predictions.recommendedActions.push('show_comparison', 'detailed_specs');
        predictions.messaging.primary = 'Compare similar products';
      }

      if (intent === 'checkout_intent') {
        predictions.recommendedActions.push('prioritize_checkout', 'show_trust_badges');
        predictions.messaging.primary = 'Complete your order - Free shipping included!';
      }

      // Store predictions for other scripts to use
      sessionStorage.setItem('ac_predictions', JSON.stringify(predictions));

      return predictions;
    },

    // A/B test variant assignment (for future optimization)
    assignABVariant: function (testName, variants) {
      const userId = this.getUserId();
      const hash = this.simpleHash(userId + testName);
      const variantIndex = hash % variants.length;

      const assignment = {
        test: testName,
        variant: variants[variantIndex],
        userId: userId,
      };

      // Store assignment
      const tests = JSON.parse(localStorage.getItem('ab_tests') || '{}');
      tests[testName] = assignment;
      localStorage.setItem('ab_tests', JSON.stringify(tests));

      return assignment;
    },

    // Get or create user ID
    getUserId: function () {
      let userId = localStorage.getItem('user_id');
      if (!userId) {
        userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('user_id', userId);
      }
      return userId;
    },

    // Simple hash function for variant assignment
    simpleHash: function (str) {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash;
      }
      return Math.abs(hash);
    },

    // Initialize all predictions
    init: function () {
      // Run predictions on page load
      const segment = this.segmentCustomer();
      const predictions = this.predictNextAction();

      // Track segments for analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'customer_segmented',
          segment: segment,
          predictions: predictions,
        });
      }

      // Make predictions available globally
      window.customerSegment = segment;
      window.predictions = predictions;

      // Run demand prediction for product pages
      if (page.pageType === 'product' && page.product) {
        this.predictDemand(page.product.id);
      }
    },
  };

  // Auto-initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => PredictiveInsights.init());
  } else {
    PredictiveInsights.init();
  }

  // Make available globally
  window.PredictiveInsights = PredictiveInsights;
})();
